import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import comptabiliteService, { CompteComptable, TypeCompte } from '../../api/comptabilite';

const TYPES: TypeCompte[] = ['ACTIF', 'PASSIF', 'CHARGE', 'PRODUIT', 'ACTIF_PASSIF'];

const emptyForm = { numero: '', libelle: '', type: 'ACTIF_PASSIF' as TypeCompte, lettrable: false };

const PlanComptable: React.FC = () => {
  const [comptes, setComptes] = useState<CompteComptable[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [classe, setClasse] = useState<number | 'all'>('all');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [version, setVersion] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [list, v] = await Promise.all([
        comptabiliteService.listComptes(),
        comptabiliteService.planVersion(),
      ]);
      setComptes(list);
      setVersion(v.version);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return comptes.filter((c) => {
      if (classe !== 'all' && c.classe !== classe) return false;
      if (!q) return true;
      return c.numero.toLowerCase().includes(q) || c.libelle.toLowerCase().includes(q);
    });
  }, [comptes, search, classe]);

  const create = async () => {
    if (!form.numero || !form.libelle) {
      toast.error('Numéro et libellé requis');
      return;
    }
    try {
      await comptabiliteService.createCompte({
        numero: form.numero,
        libelle: form.libelle,
        type: form.type,
        lettrable: form.lettrable,
        classe: Number(form.numero[0]) || 0,
      });
      toast.success('Compte créé');
      setShowForm(false);
      setForm(emptyForm);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur');
    }
  };

  const remove = async (c: CompteComptable) => {
    if (!window.confirm(`Supprimer le compte ${c.numero} — ${c.libelle} ?`)) return;
    try {
      await comptabiliteService.deleteCompte(c.id);
      toast.success('Compte supprimé');
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur');
    }
  };

  const sync = async () => {
    try {
      const res = await comptabiliteService.syncPlan();
      toast.success(`Plan synchronisé (${res.ajoutes} compte(s) ajouté(s)) — ${res.version}`);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Plan comptable</h1>
          <p className="text-sm text-gray-500">
            Système Comptable des Entreprises tunisien — classes 1 à 7. {version && `Réf. ${version}.`}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={sync}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
            Synchroniser le plan
          </button>
          <button onClick={() => setShowForm((s) => !s)}
                  className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">
            + Nouveau compte
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white p-5 rounded-xl border border-gray-200 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium mb-1">Numéro</label>
            <input value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })}
                   placeholder="ex. 6111" className="w-full rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium mb-1">Libellé</label>
            <input value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })}
                   className="w-full rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as TypeCompte })}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2">
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.lettrable}
                     onChange={(e) => setForm({ ...form, lettrable: e.target.checked })} />
              Lettrable
            </label>
            <button onClick={create}
                    className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">
              Créer
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Rechercher (numéro ou libellé)…"
               className="flex-1 min-w-[220px] rounded-lg border border-gray-200 px-3 py-2" />
        <select value={classe} onChange={(e) => setClasse(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                className="rounded-lg border border-gray-200 px-3 py-2">
          <option value="all">Toutes les classes</option>
          {[1, 2, 3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>Classe {n}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Numéro</th>
              <th className="text-left px-4 py-2 font-medium">Libellé</th>
              <th className="text-left px-4 py-2 font-medium">Classe</th>
              <th className="text-left px-4 py-2 font-medium">Type</th>
              <th className="text-left px-4 py-2 font-medium">Lettrable</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Chargement…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Aucun compte</td></tr>
            ) : filtered.map((c) => (
              <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 font-mono">{c.numero}</td>
                <td className="px-4 py-2">{c.libelle}</td>
                <td className="px-4 py-2">{c.classe}</td>
                <td className="px-4 py-2">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{c.type}</span>
                </td>
                <td className="px-4 py-2">{c.lettrable ? 'Oui' : '—'}</td>
                <td className="px-4 py-2 text-right">
                  {!c.systeme && (
                    <button onClick={() => remove(c)} className="text-red-600 hover:underline text-xs">
                      Supprimer
                    </button>
                  )}
                  {c.systeme && <span className="text-xs text-gray-300">base</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400">
        {filtered.length} compte(s). Les comptes « base » proviennent du référentiel et ne sont pas supprimables
        (vous pouvez les désactiver).
      </p>
    </div>
  );
};

export default PlanComptable;
